import React from "react";
import { Box, Button, Stack } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { DeleteForever } from "@mui/icons-material";
import Badge from "@mui/material/Badge";
import Menu from "@mui/material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useHistory } from "react-router-dom";
import { CartItem } from "../../../lib/types/search";
import { Messages, serverApi } from "../../../lib/config";
import { sweetErrorHandling } from "../../../lib/sweetAlert";
import { useGlobals } from "../../hooks/useGlobal";
import OrderService from "../../services/OrderService";

interface BasketProps {
  cartItems: CartItem[];
  onAdd: (item: CartItem) => void;
  onRemove: (item: CartItem) => void;
  onDelete: (item: CartItem) => void;
  onDeleteAll: () => void;
}

export default function Basket(props: BasketProps) {
  const { cartItems, onAdd, onRemove, onDelete, onDeleteAll } = props;
  const { authMember, setOrderBuilder } = useGlobals();
  const history = useHistory();
  const itemPrice = cartItems.reduce(
    (a: number, c: CartItem) => a + c.quantity * c.price,
    0
  );
  const shippingCost = cartItems.length > 0 ? 5 : 0;
  const totalPrice = (itemPrice + shippingCost).toFixed(1);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const proceedOrderHandler = async () => {
    try {
      handleClose();
      if (!authMember) throw new Error(Messages.error2);

      const order = new OrderService();
      await order.createOrder(cartItems);

      onDeleteAll();
      setOrderBuilder(new Date());
      history.push("/orders");
    } catch (err) {
      console.log(err);
      sweetErrorHandling(err).then();
    }
  };

  const imageUrl = (image?: string) => {
    if (!image) return "/icons/noimage-list.svg";
    if (image.startsWith("http")) return image;
    return `${serverApi}/${image}`.replace(/([^:]\/)\/+/g, "$1");
  };

  return (
    <Box className="hover-line">
      <IconButton
        aria-label="cart"
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        <Badge
          badgeContent={cartItems.length}
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: "#c9a961",
              color: "#1a120f",
              fontWeight: 700,
            },
          }}
        >
          <img src="/icons/shopping-cart.svg" alt="cart" />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          className: "bc-basket-paper",
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 8px 24px rgba(26,18,15,0.22))",
            mt: 1.5,
            borderRadius: "14px",
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "#fbf8f3",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Stack className="bc-basket">
          <Box className="bc-basket__header">
            {cartItems.length === 0 ? (
              <span className="bc-basket__title">Cart is empty</span>
            ) : (
              <>
                <span className="bc-basket__title">Cart Products</span>
                <IconButton
                  size="small"
                  aria-label="clear cart"
                  onClick={() => onDeleteAll()}
                  className="bc-basket__clear"
                >
                  <DeleteForever fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>

          {cartItems.length > 0 && (
            <Box className="bc-basket__list">
              {cartItems.map((item: CartItem) => (
                <Box className="bc-basket__item" key={item._id}>
                  <img
                    src={imageUrl(item.image)}
                    alt={item.name}
                    className="bc-basket__thumb"
                  />
                  <Box className="bc-basket__meta">
                    <span className="bc-basket__name">{item.name}</span>
                    <span className="bc-basket__price">
                      ${item.price} × {item.quantity}
                    </span>
                    <Box className="bc-basket__qty">
                      <button
                        type="button"
                        className="bc-basket__qty-btn"
                        onClick={() => onRemove(item)}
                      >
                        −
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        type="button"
                        className="bc-basket__qty-btn"
                        onClick={() => onAdd(item)}
                      >
                        +
                      </button>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    aria-label="remove item"
                    className="bc-basket__remove"
                    onClick={() => onDelete(item)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {cartItems.length > 0 && (
            <Box className="bc-basket__footer">
              <span className="bc-basket__total">
                Total: ${totalPrice}
                <small>
                  (${itemPrice} + ${shippingCost} shipping)
                </small>
              </span>
              <Button
                onClick={proceedOrderHandler}
                startIcon={<ShoppingCartIcon />}
                variant="contained"
                className="bc-basket__order-btn"
              >
                Order
              </Button>
            </Box>
          )}
        </Stack>
      </Menu>
    </Box>
  );
}
